import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  Select,
  Space,
  Tag,
  TreeSelect,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  DownOutlined,
  UpOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import {
  getAssets,
  getAssetCategories,
  getPurchaseRecords,
} from "../api/asset";
import { getUserList } from "../api/user";
import AssetDrawer from "../components/assets/AssetDrawer";
import RemoteSelect from "../components/common/RemoteSelect";
import HostSelect from "../components/host/HostSelect";
const { Text, Title } = Typography;
const STATUS = {
  warehoused: ["已入库", "#10b981", "#ecfdf5"],
  inUse: ["使用中", "#3b82f6", "#eff6ff"],
  repairing: ["维修中", "#f59e0b", "#fffbeb"],
  sold: ["已出售", "#8b5cf6", "#f5f3ff"],
  scrapped: ["已报废", "#ef4444", "#fef2f2"],
};
const STATUS_OPTIONS = Object.entries(STATUS).map(([value, [label]]) => ({
  value,
  label,
}));
const SOURCE = {
  purchase: "采购",
  unregisted: "未登记",
  repair: "维修",
  scrap: "报废",
};
const PAGE_SIZE = 20;
const TEXT_FIELDS = ["assetNo", "assetName"];
const MIN_LEN = { assetNo: 3, assetName: 2 }; // 即时查询时，文本短于该长度不算筛选条件

const asTree = (items = []) =>
  items.map((item) => ({
    title: item.name,
    value: item._id,
    key: item._id,
    children: item.children?.length ? asTree(item.children) : undefined,
  }));

// 把表单值整理成查询条件（空值统一为 undefined，便于比较是否变化）
const normalize = (all = {}) => ({
  assetNo: all.assetNo?.trim() || undefined,
  assetName: all.assetName?.trim() || undefined,
  assetType: all.assetType || undefined,
  purchaseRecord: all.purchaseRecord || undefined,
  status: all.status?.length ? all.status : undefined,
  owner: all.owner || undefined,
  location: all.location || undefined,
});

/* ---------- 远程搜索数据源：放在模块顶层，引用稳定 ---------- */
const fetchPurchases = async (value) => {
  const { data } = await getPurchaseRecords({
    purchaseBatch: value,
    pageSize: 20,
  });
  return (data?.list || []).map((r) => ({
    label: `${r.purchaseBatch || "-"} · ${r.purchaser?.name || "-"}`,
    value: r._id,
  }));
};
// label 只放姓名（选中后输入框里保持简洁），证件号放在 identityNumber，仅在下拉项里展示
const toUserOption = (u) => ({
  label: u?.name || u?.username,
  value: u._id,
  identityNumber: u?.identityNumber || undefined,
});
const renderUserOption = (option) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
    }}
  >
    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
      {option.label}
    </span>
    {option.data?.identityNumber && (
      <span style={{ flexShrink: 0, color: "#94a3b8", fontSize: 12 }}>
        {option.data.identityNumber}
      </span>
    )}
  </div>
);
const fetchUsers = async (name) => {
  const { data } = await getUserList({ name });
  return (data || []).map(toUserOption);
};

// 必须定义在模块顶层：定义在组件内部会导致每次渲染都重建整个表单，输入框失焦
const FilterItem = ({ label, name, last, children }) => (
  <div className="asset-filter-row" style={{ marginBottom: last ? 0 : 8 }}>
    <span className="asset-filter-label">{label}</span>
    <Form.Item name={name} noStyle>
      {children}
    </Form.Item>
  </div>
);

// memo：props 只剩 4 个且引用稳定，列表加载不会重渲染筛选表单
const FilterForm = React.memo(function FilterForm({
  form,
  categories,
  onValuesChange,
  commit,
}) {
  // 排查用：输入时若反复输出 mount/unmount，说明表单被重建了
  // useEffect(() => { console.log("FilterForm mount"); return () => console.log("FilterForm unmount"); }, []);
  return (
    <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
      <FilterItem label="资产编号" name="assetNo">
        <Input
          allowClear
          enterKeyHint="search"
          placeholder="至少输入 3 个字符"
          onBlur={commit}
          onPressEnter={commit}
        />
      </FilterItem>
      <FilterItem label="资产名称" name="assetName">
        <Input
          allowClear
          enterKeyHint="search"
          placeholder="至少输入 2 个字符"
          onBlur={commit}
          onPressEnter={commit}
        />
      </FilterItem>
      <FilterItem label="资产类型" name="assetType">
        <TreeSelect
          showSearch
          allowClear
          treeDefaultExpandAll
          treeNodeFilterProp="title"
          treeData={categories}
          placeholder="选择或输入类型"
        />
      </FilterItem>
      <FilterItem label="采购单" name="purchaseRecord">
        <RemoteSelect
          fetcher={fetchPurchases}
          notFoundContent="未找到采购单"
          placeholder="输入批次号搜索"
        />
      </FilterItem>
      <FilterItem label="资产状态" name="status">
        <Select
          mode="multiple"
          maxTagCount={2}
          allowClear
          placeholder="全部状态"
          options={STATUS_OPTIONS}
        />
      </FilterItem>
      <FilterItem label="负责人" name="owner">
        <RemoteSelect
          fetcher={fetchUsers}
          optionRender={renderUserOption}
          notFoundContent="未找到负责人"
          placeholder="输入姓名搜索"
        />
      </FilterItem>
      <FilterItem label="所在位置" name="location" last>
        <HostSelect placeholder="至少输入 3 个字符搜索" />
      </FilterItem>
    </Form>
  );
});

export default function AssetManagementPage() {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editorUserOptions, setEditorUserOptions] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const requestId = useRef(0);
  const valuesRef = useRef({});
  const failedRef = useRef(false); // 最近一次列表请求是否失败：失败后相同条件允许重试

  const fetchRows = useCallback(
    async (nextPage = 1, values = valuesRef.current) => {
      const id = ++requestId.current;
      valuesRef.current = values;
      setLoading(true);
      try {
        const { data } = await getAssets({
          page: nextPage,
          pageSize: PAGE_SIZE,
          ...values,
        });
        if (id !== requestId.current) return;
        setRows(data.list || []);
        setTotal(data.total || 0);
        setPage(nextPage);
        failedRef.current = false;
      } catch (error) {
        if (id === requestId.current) {
          failedRef.current = true;
          message.error("资产列表加载失败");
        }
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    getAssetCategories()
      .then(({ data }) => setCategories(asTree(data)))
      .catch(() => {});
    getUserList()
      .then(({ data }) => setEditorUserOptions((data || []).map(toUserOption)))
      .catch(() => {});
    fetchRows(1, {});
  }, [fetchRows]);

  // 读取当前表单值并查询；与上次查询条件相同则跳过（失焦和回车可能连续触发）
  const commit = useCallback(() => {
    const values = normalize(form.getFieldsValue());
    if (
      !failedRef.current &&
      JSON.stringify(values) === JSON.stringify(valuesRef.current)
    )
      return;
    fetchRows(1, values);
  }, [form, fetchRows]);

  // 文本框输入中的即时查询（防抖）。
  // 请求只会改变页面自己的 state，FilterForm 已被 memo 隔离，不会因此重渲染，所以不影响输入焦点。
  const instantSearch = useMemo(
    () =>
      debounce(() => {
        const values = normalize(form.getFieldsValue());
        for (const [key, min] of Object.entries(MIN_LEN)) {
          // 过短的文本不算筛选条件：沿用上次的值，等输入够长再查
          if (values[key] && values[key].length < min)
            values[key] = valuesRef.current[key];
        }
        if (
          !failedRef.current &&
          JSON.stringify(values) === JSON.stringify(valuesRef.current)
        )
          return;
        fetchRows(1, values);
      }, 500),
    [form, fetchRows],
  );
  useEffect(() => () => instantSearch.cancel(), [instantSearch]);

  // 失焦 / 回车 / 下拉选择：取消待执行的防抖，立即按当前表单值查询（不受最小长度限制）
  const commitNow = useCallback(() => {
    instantSearch.cancel();
    commit();
  }, [instantSearch, commit]);

  const onValuesChange = useCallback(
    (changed) => {
      const textOnly = Object.keys(changed).every((k) =>
        TEXT_FIELDS.includes(k),
      );
      if (textOnly)
        instantSearch(); // 文本输入：防抖后查询
      else commitNow(); // 下拉选择、清除：立即查询
    },
    [instantSearch, commitNow],
  );

  const clearFilters = () => {
    form.resetFields();
    fetchRows(1, {});
  };
  const openEdit = (asset) => {
    setSelectedAsset(asset);
    setDrawerOpen(true);
  };
  const openCreate = () => {
    setSelectedAsset(null);
    setDrawerOpen(true);
  };
  const onSaved = () => {
    setDrawerOpen(false);
    fetchRows(1, valuesRef.current);
  };
  const statusTag = (status) => {
    const [label, color, bg] = STATUS[status] || [
      status || "—",
      "#64748b",
      "#f1f5f9",
    ];
    return (
      <Tag
        bordered={false}
        style={{
          margin: 0,
          color,
          background: bg,
          borderRadius: 20,
          fontWeight: 600,
        }}
      >
        {label}
      </Tag>
    );
  };

  return (
    <div
      style={{
        minHeight: "100dvh",
        padding: "16px 12px calc(24px + env(safe-area-inset-bottom))",
        background: "#f5f7fa",
        maxWidth: 760,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0, color: "#1f2329" }}>
            资产管理
          </Title>
          <Text type="secondary">共 {total} 项资产</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          style={{ height: 42, borderRadius: 10 }}
        >
          新增资产
        </Button>
      </div>

      <Card
        styles={{ body: { padding: 0 } }}
        style={{ marginBottom: 12, borderRadius: 12, overflow: "hidden" }}
      >
        <button
          type="button"
          onClick={() => setFiltersOpen((open) => !open)}
          style={{
            width: "100%",
            height: 50,
            border: 0,
            background: "#fff",
            padding: "0 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#313740",
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          <span>筛选资产</span>
          <Space>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                clearFilters();
              }}
            >
              清空
            </Button>
            {filtersOpen ? <UpOutlined /> : <DownOutlined />}
          </Space>
        </button>
        {/* 用 display 控制显隐而不是条件渲染：收起再展开时表单不会被卸载，输入内容保留 */}
        <div
          style={{
            display: filtersOpen ? "block" : "none",
            padding: "0 14px 12px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <FilterForm
            form={form}
            categories={categories}
            onValuesChange={onValuesChange}
            commit={commitNow}
          />
        </div>
      </Card>

      <style>{`
      .asset-filter-row { display: flex; align-items: center; gap: 10px; min-width: 0; }
      .asset-filter-label { flex: 0 0 68px; color: #64748b; font-size: 13px; white-space: nowrap; overflow: hidden; transition: flex-basis .15s, opacity .15s, margin-right .15s; }
      .asset-filter-row > .ant-input-affix-wrapper,
      .asset-filter-row > .ant-input,
      .asset-filter-row > .ant-select,
      .asset-filter-row > .ant-tree-select { flex: 1; min-width: 0; }
      .asset-filter-row:focus-within .asset-filter-label { flex-basis: 0; opacity: 0; margin-right: -10px; }
    `}</style>

      <Space
        direction="vertical"
        size={10}
        style={{
          width: "100%",
          opacity: loading ? 0.55 : 1,
          transition: "opacity .2s",
        }}
      >
        {rows.map((asset) => (
          <Card
            key={asset._id}
            onClick={() => openEdit(asset)}
            hoverable
            loading={false}
            style={{ borderRadius: 12, cursor: "pointer" }}
            styles={{ body: { padding: "14px 14px 12px" } }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
                marginBottom: 9,
              }}
            >
              <Text
                ellipsis
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: "ui-monospace,monospace",
                  color: "#475569",
                  fontSize: 13,
                }}
              >
                {asset.assetNo || "—"}
              </Text>
              {statusTag(asset.status)}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
                marginBottom: 10,
              }}
            >
              <Text
                strong
                ellipsis
                style={{ flex: 1, minWidth: 0, color: "#1f2329", fontSize: 15 }}
              >
                {asset.assetName || "—"}
              </Text>
              {asset.assetType?.name && (
                <span
                  style={{
                    flexShrink: 0,
                    maxWidth: "42%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: "#f1f5f9",
                    color: "#475569",
                    fontSize: 11,
                  }}
                >
                  {asset.assetType.name}
                </span>
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 6,
                color: "#64748b",
                fontSize: 12,
                marginBottom: 10,
              }}
            >
              <span>
                {asset.warehousedAt
                  ? dayjs(asset.warehousedAt).format("YYYY-MM-DD")
                  : "—"}
              </span>
              <span>
                {SOURCE[asset.assetSource] || asset.assetSource || "—"}
              </span>
              <span style={{ color: "#1f2329", fontWeight: 600 }}>
                ¥{Number(asset.originalValue || 0).toLocaleString()}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                borderTop: "1px solid #f5f5f5",
                paddingTop: 9,
              }}
            >
              <span
                style={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  padding: "3px 8px",
                  borderRadius: 5,
                  background: "#eff6ff",
                  color: "#3b82f6",
                  fontSize: 11,
                  border: "1px solid #bfdbfe",
                }}
              >
                <EnvironmentOutlined style={{ marginRight: 3 }} />
                {asset.location?.roomNumber || "—"}
              </span>
              <Text style={{ flexShrink: 0, color: "#94a3b8", fontSize: 11 }}>
                {asset.updatedAt
                  ? dayjs(asset.updatedAt).format("YYYY-MM-DD")
                  : "—"}
              </Text>
            </div>
          </Card>
        ))}
        {!loading && rows.length === 0 && (
          <Card style={{ borderRadius: 12 }}>
            <Empty description="暂无资产" />
          </Card>
        )}
      </Space>
      {total > PAGE_SIZE && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 16,
            alignItems: "center",
            padding: 16,
          }}
        >
          <Button disabled={page <= 1} onClick={() => fetchRows(page - 1)}>
            上一页
          </Button>
          <Text>
            {page} / {Math.ceil(total / PAGE_SIZE)}
          </Text>
          <Button
            disabled={page >= Math.ceil(total / PAGE_SIZE)}
            onClick={() => fetchRows(page + 1)}
          >
            下一页
          </Button>
        </div>
      )}
      {loading && rows.length === 0 && (
        <Card loading style={{ marginTop: 12, borderRadius: 12 }} />
      )}
      <AssetDrawer
        open={drawerOpen}
        asset={selectedAsset}
        categoryOptions={categories}
        userOptions={editorUserOptions}
        onClose={() => setDrawerOpen(false)}
        onSave={onSaved}
      />
    </div>
  );
}
